import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { OrganizationBrandingContent } from './api/types';
import { useApi } from './api/api-provider';
import { useSession } from './session/session-provider';
import { useBrandingAsset } from './components/use-branding-asset';

interface OrganizationBrandingValue {
  readonly active: OrganizationBrandingContent | null;
  readonly logoUrl: string | undefined;
  readonly heroUrl: string | undefined;
  readonly status: 'idle' | 'loading' | 'success' | 'error';
  readonly reload: () => void;
}
const EMPTY: OrganizationBrandingValue = { active: null, logoUrl: undefined, heroUrl: undefined, status: 'idle', reload: () => undefined };
const OrganizationBrandingContext = createContext<OrganizationBrandingValue>(EMPTY);

/** Identity-keyed ownership prevents even a single render of another tenant's branding. */
export function OrganizationBrandingProvider({ children }: { readonly children: ReactNode }) {
  const { tenant, user, status } = useSession();
  const key = `${status}:${tenant?.id ?? ''}:${user?.subject ?? ''}`;
  return <ScopedOrganizationBrandingProvider key={key}>{children}</ScopedOrganizationBrandingProvider>;
}

function ScopedOrganizationBrandingProvider({ children }: { readonly children: ReactNode }) {
  const api = useApi();
  const { isAuthenticated, hasPermission, toApiSession } = useSession();
  const [value, setValue] = useState<Pick<OrganizationBrandingValue, 'active' | 'status'>>({ active: null, status: 'idle' });
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((current) => current + 1), []);
  const readable = isAuthenticated && hasPermission('branding:read');
  const logo = useBrandingAsset(readable ? value.active?.logoAssetId : null, 'logo');
  const hero = useBrandingAsset(readable ? value.active?.heroAssetId : null, 'hero');

  useEffect(() => {
    if (!readable) return;
    let alive = true;
    setValue({ active: null, status: 'loading' });
    const session = toApiSession();
    void api.getOrganizationBranding(session).then((response) => {
      if (!alive) return;
      setValue({ active: response.active, status: 'success' });
    }).catch(() => {
      if (alive) setValue({ active: null, status: 'error' });
    });
    return () => {
      alive = false;
    };
  }, [api, readable, toApiSession, revision]);

  useEffect(() => {
    window.addEventListener('event:branding-changed', reload);
    return () => window.removeEventListener('event:branding-changed', reload);
  }, [reload]);

  return <OrganizationBrandingContext.Provider value={{ ...(readable ? { ...value, logoUrl: logo.url, heroUrl: hero.url } : EMPTY), reload }}>{children}</OrganizationBrandingContext.Provider>;
}

export function useOrganizationBranding(): OrganizationBrandingValue {
  return useContext(OrganizationBrandingContext);
}
